import { router, useLocalSearchParams } from "expo-router";
import React, { useRef, useState } from "react";
import { View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Pressable from "$src/core/components/atoms/Pressable";
import ScrollView from "$src/core/components/atoms/ScrollView/ScrollView";
import Spinner from "$src/core/components/atoms/Spinner/Spinner";
import Text from "$src/core/components/atoms/Text/Text";
import type { SearchBoxRef } from "$src/core/components/molecules/SearchBox/SearchBox";
import SearchBox from "$src/core/components/molecules/SearchBox/SearchBox";
import SearchResultCard from "$src/core/components/molecules/SearchResultCard/SearchResultCard";
import SwimmingLane from "$src/core/components/organisms/SwimmingLane";
import useAnalytics from "$src/core/hooks/use-analytics";
import useSearch from "$src/core/hooks/use-search";
import useSearchHistory from "$src/core/hooks/use-search-history";
import useStorageState from "$src/core/hooks/use-storage-state";
import useTabBarHeight from "$src/core/hooks/use-tab-bar-height";
import useTranslation from "$src/core/hooks/use-translation";
import useVoiceSearch from "$src/core/hooks/use-voice-search";
import type { ContentDto } from "$src/core/models/dto";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import MessagePage from "$src/core/pages/MessagePage/MessagePage";
import useSearchRecommendationsQuery from "$src/core/queries/use-search-recommendations-query";
import Alerting from "$src/core/services/alerting";
import Logging from "$src/core/services/logging/logging";
import $pageInsets from "$src/core/styles/page";

import $ from "./SearchPage.styles";

const SearchPage = () => {
  const { query: queryParam } = useLocalSearchParams<{ query?: string }>();

  const tabBarHeight = useTabBarHeight();
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { history, addToHistory, clearHistory } = useSearchHistory();

  const { data: recommendations } = useSearchRecommendationsQuery();

  const searchBoxRef = useRef<SearchBoxRef | null>(null);

  const [query, setQuery] = useState(queryParam ?? "");
  const [aiStatus] = useStorageState("search.conversational.status", null);

  const { status, results, relatedResults, enableConversationalSearch, retry } =
    useSearch({
      query,
    });

  const handleSearch = (value: string) => {
    if (value.trim().length < 2) return;

    setQuery(value);
    trackEvent("search", {
      search_type: "search_query",
      search_query: value,
    });
    router.setParams({ query: value });
  };

  const handleClear = () => {
    setQuery("");
    router.setParams({ query: "" });
  };

  const handleSearchFocus = () => {
    if (aiStatus !== null) return;

    // Opts in silently on an OS model; only the ExecuTorch fallback prompts.
    enableConversationalSearch().catch((error) => {
      Logging.error("Failed to enable conversational search", { error });
    });
  };

  const handleClearHistory = () => {
    Alerting.prompt(t("SEARCH/RECENT/CLEAR_CONFIRM"), {
      confirmLabel: t("COMMON/CLEAR"),
      cancelLabel: t("COMMON/CANCEL"),
      onConfirm: clearHistory,
    });
  };

  const handleItemPress = (item: ContentDto) => {
    router.push(`/${item.type}/${item.id}`);
    addToHistory(item);
  };

  const voiceSearch = useVoiceSearch({
    onTranscript: (text) => {
      searchBoxRef.current?.setValue(text);
      handleSearch(text);
    },
  });

  const hasResults = results.length > 0 || relatedResults.length > 0;

  // Only take over the whole page when nothing loaded. A single failed facet
  // lane (or a classification error while a plain search succeeded) still
  // renders whatever did resolve.
  if (query && status === "error" && !hasResults) {
    return (
      <ErrorPage
        title={t("ERROR/SEARCH_PAGE/TITLE")}
        subtitle={t("ERROR/SEARCH_PAGE/SUBTITLE")}
        ctaText={t("COMMON/RETRY")}
        ctaIcon="retry"
        onPressCta={() => {
          retry().catch((error) =>
            Logging.error("Failed to retry search", { error }),
          );
        }}
      />
    );
  }

  return (
    <ScrollView
      contentContainerStyle={$pageInsets.vertical({ tabBarHeight })}
      accessibilityRole="list"
      showsVerticalScrollIndicator={false}
    >
      <View style={[$.search, $pageInsets.horizontal]}>
        <SearchBox
          ref={searchBoxRef}
          defaultValue={query}
          onSearch={handleSearch}
          onClear={handleClear}
          onFocus={handleSearchFocus}
          voiceSearch={
            voiceSearch.isSupported
              ? {
                  isListening: voiceSearch.isListening,
                  audioLevel: voiceSearch.audioLevel,
                  onMicPress: voiceSearch.toggleListening,
                }
              : undefined
          }
        />
      </View>

      {query ? (
        status === "pending" ? (
          <View style={$pageInsets.horizontal}>
            <Spinner />
          </View>
        ) : (
          <>
            {results.length > 0 && (
              <View style={$.section}>
                <View style={[$.results, $pageInsets.horizontal]}>
                  {results.map((item) => (
                    <Pressable
                      key={item.id}
                      accessibilityRole="button"
                      accessibilityLabel={item.title}
                      onPress={() => handleItemPress(item)}
                    >
                      <SearchResultCard
                        title={item.title}
                        description={item.subtitle}
                        image={item.image ?? item.images}
                      />
                    </Pressable>
                  ))}
                </View>
              </View>
            )}

            {relatedResults.map(({ tag, items }) => (
              <View key={tag} style={$.section}>
                <Text
                  preset="heading3"
                  accessibilityRole="text"
                  style={$pageInsets.horizontal}
                >
                  {tag}
                </Text>
                <SwimmingLane
                  type="portrait"
                  items={items}
                  accessibilityLaneTitle={tag}
                  contentContainerStyle={[
                    $.recommendationsLane,
                    $pageInsets.horizontal,
                  ]}
                />
              </View>
            ))}

            {!hasResults && (
              <MessagePage
                title={t("SEARCH/NO_RESULTS/TITLE")}
                subtitle={t("SEARCH/NO_RESULTS/SUBTITLE")}
                icon="search"
              />
            )}
          </>
        )
      ) : (
        <>
          {!!history?.length && (
            <View style={$.section}>
              <View style={[$.recentHeader, $pageInsets.horizontal]}>
                <Text
                  preset="heading3"
                  accessibilityRole="text"
                  numberOfLines={1}
                  style={$.recentTitle}
                >
                  {t("SEARCH/RECENT/TITLE")}
                </Text>
                <View style={$.clearButton}>
                  <Button
                    name="clear_recent_searches"
                    preset="link"
                    onPress={handleClearHistory}
                    label={t("COMMON/CLEAR")}
                  />
                </View>
              </View>
              <View style={[$.results, $pageInsets.horizontal]}>
                {history.map((item) => (
                  <Pressable
                    key={item.id}
                    accessibilityRole="button"
                    accessibilityLabel={item.title}
                    onPress={() => handleItemPress(item)}
                  >
                    <SearchResultCard
                      title={item.title}
                      description={item.subtitle}
                      image={item.image ?? item.images}
                    />
                  </Pressable>
                ))}
              </View>
            </View>
          )}

          {!!recommendations?.items.length && (
            <View style={$.section}>
              <Text
                preset="heading3"
                accessibilityRole="text"
                style={$pageInsets.horizontal}
              >
                {t("SEARCH/RECOMMENDATIONS/TITLE")}
              </Text>
              <SwimmingLane
                type="portrait"
                items={recommendations.items}
                accessibilityLaneTitle={t("SEARCH/RECOMMENDATIONS/TITLE")}
                contentContainerStyle={[
                  $.recommendationsLane,
                  $pageInsets.horizontal,
                ]}
              />
            </View>
          )}
        </>
      )}
    </ScrollView>
  );
};

export default SearchPage;
